/**
 * Recursively omit `undefined` values so payloads are safe for Firestore writes.
 * Firestore rejects undefined field values, including nested ones inside arrays
 * ("Property array contains an invalid nested entity").
 *
 * `null` is preserved. Array holes / undefined elements are dropped.
 */
export function stripUndefinedDeep(value) {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedDeep(item))
      .filter((item) => item !== undefined);
  }

  // Leave non-plain objects alone (Date, Firestore Timestamp, FieldValue, etc.).
  if (Object.getPrototypeOf(value) !== Object.prototype) {
    return value;
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (entryValue === undefined) {
      return accumulator;
    }

    const cleanedValue = stripUndefinedDeep(entryValue);
    if (cleanedValue !== undefined) {
      accumulator[key] = cleanedValue;
    }
    return accumulator;
  }, {});
}
